import { ArrowRightIcon, SpinnerIcon } from "@phosphor-icons/react/dist/ssr";
import { Slot } from "radix-ui";
import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "@/core/shared/v2/lib/utils";

const Loader = () => <SpinnerIcon className="animate-spin" />;

export interface ButtonProps extends ComponentPropsWithRef<"button"> {
  variant?: "primary" | "secondary" | "tertiary" | "destructive" | "ghost" | "link" | "transparent";
  size?: "default" | "sm" | "lg" | "xl" | "icon";
  loading?: boolean;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  arrowOnHover?: boolean;
  asChild?: boolean;
}

export function Button({
  disabled = false,
  loading = false,
  asChild = false,
  variant = "primary",
  size = "default",
  arrowOnHover = variant === "primary" || variant === "secondary",
  children,
  className,
  leftSection = null,
  rightSection = null,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const isLoading = !disabled && loading;
  const Component = asChild && !isDisabled ? Slot.Root : "button";
  const customProps = Component === "button" ? ({ disabled: isDisabled, type: "button" } as const) : {};

  const isIconOnly = size === "icon";
  const leftElement = isLoading ? <Loader /> : leftSection;
  const rightElement = rightSection ?? (arrowOnHover ? <ArrowRightIcon data-arrow /> : null);

  const baseStyles =
    "inline-flex cursor-pointer items-center justify-center gap-2 rounded-full whitespace-nowrap ring-offset-background transition-colors duration-200 select-none active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0";

  const variantStyles = {
    primary: "bg-primary text-primary-foreground hover:bg-dodger-blue-700",
    secondary:
      "border border-primary text-primary hover:border-dodger-blue-700 hover:bg-dodger-blue-700 hover:text-primary-foreground",
    tertiary: "bg-secondary text-secondary-foreground hover:bg-stone-200",
    destructive: "bg-destructive text-destructive-foreground hover:bg-destructive",
    ghost: "border-none text-primary hover:bg-stone-200",
    link: "border-none text-primary underline hover:text-primary active:scale-100 active:text-primary",
    transparent:
      "border border-primary-foreground bg-transparent text-primary-foreground hover:border-dodger-blue-700 hover:bg-dodger-blue-700",
  };

  const sizeStyles = {
    sm: "h-8 px-3 text-xs font-medium",
    default: "h-10 px-4 text-sm font-semibold",
    lg: "h-12 px-8 text-sm font-semibold",
    xl: "h-14 px-8 text-base font-semibold",
    icon: "size-10",
  };

  // Handling the arrow animation and gap logic
  const arrowStyles = arrowOnHover
    ? "[&_svg[data-arrow]]:size-0 [&_svg[data-arrow]]:transition-all [&_svg[data-arrow]]:duration-200 hover:[&_svg[data-arrow]]:ml-2 hover:[&_svg[data-arrow]]:static hover:[&_svg[data-arrow]]:size-4 has-[svg[data-arrow]]:gap-0 [&_svg:first-child:not([data-arrow])]:mr-2"
    : "";

  return (
    <Component
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(baseStyles, variantStyles[variant], sizeStyles[size], arrowStyles, className)}
      {...customProps}
      {...props}
    >
      {!isIconOnly && leftElement}
      <Slot.Slottable>{isIconOnly && isLoading ? <Loader /> : children}</Slot.Slottable>
      {!isIconOnly && rightElement}
    </Component>
  );
}
